import { useEffect, useState, useContext, useMemo } from 'react';
import { UserContext } from './UserContext';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Table, Button, Modal, Alert, Form, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from './Breadcrumb.js';
import UserAddGeneral from './UserAddGeneral.js';
import api from './api/axiosInstance.js';
import ProfileMobileTabs from './ProfileMobileTabs';
import icSucess from './assets/img/ic-celebration-circle.svg';
import icEye from './assets/img/ic-eye.svg';
import icEyeClosed from './assets/img/ic-eye-closed.svg';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { useMediaQuery } from 'react-responsive';
import MobileDiscoverySearchToolbar from './components/Discovery/MobileDiscoverySearchToolbar';
import MobileLoadMoreFooter from './components/Discovery/MobileLoadMoreFooter';
import useMobileLoadMore from './components/Discovery/useMobileLoadMore';
import MobileProfileHistoryCard from './components/Profile/MobileProfileHistoryCard';
import ProfileUpdateSuccessModal from './components/Profile/ProfileUpdateSuccessModal';
import ProfileAccountSuccessModal, { useProfileAccountSuccess } from './components/Profile/ProfileAccountSuccessModal';
import openPublicPdf from './utils/openPublicPdf';

const profileNavItems = [
    { to: '/profile', eventKey: 'about', label: 'About', end: true },
    { to: '/profile/academics', eventKey: 'academics', label: 'Academics' },
    { to: '/profile/work-preferences', eventKey: 'work', label: 'Work Preferences' },
    { to: '/profile/history', eventKey: 'history', label: 'History of changes' },
];

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const FIELD_LABELS = {
    firstName: 'First Name',
    familyName: 'Last Name',
    gender: 'Gender',
    profileImage: 'Profile Image',
    email: 'Email',
    birthDate: 'Birth Date',
    phone: 'Phone',
    company: 'Company',
    companyRole: 'Company Role',
    highestDegree: 'Highest Degree',
    currentDegree: 'Current Degree',
    currentEducationalInstitution: 'Institution',
    academicsCountry: 'Academics Country',
    academicsCity: 'Academics City',
    studyDirection: 'Study Direction',
    countriesWorkPreferences: 'Country Preferences',
    citiesWorkPreferences: 'City Preferences',
    languagesWorkPreferences: 'Language Preferences',
    industryJobPreferences: 'Industry Preferences',
    employmentJobPreferences: 'Employment Type',
    fteJobPreferences: 'FTE Preference',
    jobTypeJobPreferences: 'Job Type',
    companyGlobalSize: 'Company Global Size',
    companyLocalSize: 'Company Local Size',
    companyRating: 'Company Rating',
    fortune: 'Fortune',
    futureDegree: 'Future Degree',
    plannedGraduationYear: 'Graduation Year',
    citizenships: 'Citizenships',
    nationality: 'Nationality',
    country: 'Country',
    city: 'City',
    zipCity: 'Zip Code',
};

const UserProfileHistory = () => {
    const { user: actor, setUser } = useContext(UserContext);
    const id = String(actor?._id || '');
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ query: '(max-width: 991.98px)' });

    const [generalData, setGeneralData] = useState({
        profileImage: '', firstName: '', familyName: '', gender: '',
        company: '', companyRole: '', activityStatus: false,
    });
    const [profileImage, setProfileImage] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [errors, setErrors] = useState({});
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [history, setHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [historySearch, setHistorySearch] = useState('');

    const [showSavedModal, setShowSavedModal] = useState(false);
    const [savedModalMessage, setSavedModalMessage] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const { accountSuccessKind, showAccountSuccess, hideAccountSuccess } = useProfileAccountSuccess();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);
    const [pwdMsg, setPwdMsg] = useState('');
    const [pwdSuccess, setPwdSuccess] = useState('');
    const [pwdLoading, setPwdLoading] = useState(false);
    const [pwdFading, setPwdFading] = useState(false);

    useEffect(() => {
        if (!pwdSuccess) return;
        setPwdFading(false);
        const fadeTimer = setTimeout(() => setPwdFading(true), 4000);
        const clearTimer = setTimeout(() => { setPwdSuccess(''); setPwdFading(false); }, 5000);
        return () => { clearTimeout(fadeTimer); clearTimeout(clearTimer); };
    }, [pwdSuccess]);

    const pwdChecks = useMemo(() => {
        const p = String(newPwd || '');
        return {
            minLen: p.length >= 8,
            lower: /[a-z]/.test(p),
            upper: /[A-Z]/.test(p),
            number: /\d/.test(p),
            special: /\W/.test(p),
        };
    }, [newPwd]);

    const pwdMatch = useMemo(() => {
        if (!newPwd && !confirmPwd) return null;
        return newPwd === confirmPwd;
    }, [newPwd, confirmPwd]);

    const normalizeId = (x) => {
        if (!x) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'object') {
            if (x._id) return normalizeId(x._id);
            if (x.$oid) return String(x.$oid);
            if (x.id) return normalizeId(x.id);
        }
        return '';
    };

    useEffect(() => {
        api.get('/api/companies').then(res => setCompanies(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!id) return;
        const fetchUserData = async () => {
            setIsLoadingUser(true);
            try {
                const res = await api.get(`/api/users/${id}`);
                const u = res.data;
                if (!u) return;
                setGeneralData({
                    profileImage: u.profileImage || '',
                    firstName: u.firstName || '',
                    familyName: u.familyName || '',
                    gender: u.gender || '',
                    profileImagePreview: u.profileImage || '',
                    company: normalizeId(u.company),
                    companyRole: u.companyRole || '',
                    activityStatus: u.activityStatus || false,
                });
            } catch (err) {
                console.error('Error fetching user data:', err);
            } finally {
                setIsLoadingUser(false);
            }
        };
        fetchUserData();
    }, [id]);

    useEffect(() => {
        const fetchHistory = async () => {
            setIsLoadingHistory(true);
            try {
                const res = await api.get('/api/users/me/history');
                setHistory(res.data || []);
            } catch (err) {
                console.error('Error fetching history:', err);
            } finally {
                setIsLoadingHistory(false);
            }
        };
        fetchHistory();
    }, []);

    const validateGeneralFields = () => {
        const newErrors = {};
        if (!generalData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!generalData.familyName.trim()) newErrors.familyName = 'Last name is required';
        if (!generalData.gender) newErrors.gender = 'Gender is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGeneralChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'profileImage' && files?.length > 0) {
            const file = files[0];
            setProfileImage(file);
            setGeneralData((prev) => ({ ...prev, profileImagePreview: URL.createObjectURL(file) }));
            return;
        }
        setGeneralData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateGeneralFields()) return false;
        try {
            const data = new FormData();
            data.append('firstName', generalData.firstName || '');
            data.append('familyName', generalData.familyName || '');
            data.append('gender', generalData.gender || '');
            if (profileImage) data.append('profileImage', profileImage);
            await api.put(`/api/users/${id}`, data);
            setSavedModalMessage('General information updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (err) {
            console.error('Error updating general info:', err);
            return false;
        }
    };

    const handleGeneralReset = (resetData) => { setGeneralData(resetData); setErrors({}); };

    const isActive = generalData.activityStatus;

    const handleDeactivate = async () => {
        try {
            const endpoint = isActive ? '/api/users/me/deactivate' : '/api/users/me/activate';
            await api.put(endpoint);
            setShowDeactivateModal(false);
            showAccountSuccess(isActive ? 'deactivated' : 'activated');
            setGeneralData((prev) => ({ ...prev, activityStatus: !isActive }));
        } catch (err) {
            console.error('Error toggling account status:', err);
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete('/api/users/me');
            setShowDeleteModal(false);
            if (setUser) setUser(null);
            navigate('/');
        } catch (err) {
            console.error('Error deleting account:', err);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwdMsg('');
        setPwdSuccess('');
        if (!newPwd || !confirmPwd) { setPwdMsg('Please enter and confirm your new password.'); return; }
        if (newPwd !== confirmPwd) { setPwdMsg('Passwords do not match.'); return; }
        const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/;
        if (!passwordRegex.test(newPwd)) { setPwdMsg('Password must meet all requirements below.'); return; }
        try {
            setPwdLoading(true);
            await api.put('/api/users/me/change-password', { newPassword: newPwd });
            setNewPwd(''); setConfirmPwd('');
            setShowPasswordModal(false);
            showAccountSuccess('password');
        } catch (err) {
            setPwdMsg(err.response?.data?.msg || 'Failed to change password.');
        } finally {
            setPwdLoading(false);
        }
    };

    const flatRows = useMemo(() => {
        const rows = [];
        history.forEach((entry, entryIndex) => {
            const changedBy = entry.changedBy;
            const name = changedBy
                ? `${changedBy.firstName || ''} ${changedBy.familyName || ''}`.trim() || '—'
                : '—';
            const changes = entry.changes?.length ? entry.changes : [{ field: '', oldValue: '', newValue: '' }];
            changes.forEach((c, changeIndex) => {
                rows.push({
                    _id: `${entry._id || `history-${entryIndex}`}-${changeIndex}`,
                    entryId: entry._id,
                    date: entry.createdAt,
                    section: entry.section,
                    field: c.field,
                    oldValue: c.oldValue,
                    newValue: c.newValue,
                    name,
                });
            });
        });
        return rows;
    }, [history]);

    const normalizedHistorySearch = historySearch.trim().toLocaleLowerCase();
    const mobileHistoryRows = useMemo(() => {
        if (!normalizedHistorySearch) return flatRows;
        return flatRows.filter((row) => [
            row.section,
            FIELD_LABELS[row.field] || row.field,
            row.oldValue,
            row.newValue,
            row.name,
        ].some((value) => String(value || '').toLocaleLowerCase().includes(normalizedHistorySearch)));
    }, [flatRows, normalizedHistorySearch]);
    const mobileHistoryPagination = useMobileLoadMore({
        items: mobileHistoryRows,
        filterKey: normalizedHistorySearch,
        storageKey: 'profile-history-mobile-list',
        isMobile,
    });

    const totalPages = Math.ceil(flatRows.length / rowsPerPage);
    const currentRows = flatRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const getPaginationItems = () => {
        if (totalPages <= 1) return [1];
        const range = (s, e) => { const o = []; for (let i = s; i <= e; i++) o.push(i); return o; };
        const boundary = 1, sibling = 2;
        const startPages = range(1, Math.min(boundary, totalPages));
        const endPages = range(Math.max(totalPages - boundary + 1, boundary + 1), totalPages);
        const siblingsStart = Math.max(Math.min(currentPage - sibling, totalPages - boundary - sibling * 2 - 1), boundary + 2);
        const siblingsEnd = Math.min(Math.max(currentPage + sibling, boundary + sibling * 2 + 2), totalPages - boundary - 1);
        const items = [
            ...startPages,
            ...(siblingsStart > boundary + 2 ? ['ellipsis'] : boundary + 1 < totalPages - boundary ? [boundary + 1] : []),
            ...range(siblingsStart, siblingsEnd),
            ...(siblingsEnd < totalPages - boundary - 1 ? ['ellipsis'] : totalPages - boundary > boundary ? [totalPages - boundary] : []),
            ...endPages,
        ];
        return items.filter((v, i) => items.indexOf(v) === i);
    };

    if (isLoadingUser) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1 profile-page">
                    <Container className="profile-page__container"><p className="mt-4">Loading...</p></Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1 profile-page">
                <Container className="profile-page__container">
                    {pwdSuccess && (
                        <Row>
                            <Col>
                                <Alert variant="success" className="d-flex flex-row" style={{ transition: 'opacity 1s', opacity: pwdFading ? 0 : 1 }}>
                                    <strong>Password saved</strong>
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card className="profile-content-shell">
                                <Card.Body className="pt-0 profile-mobile">
                                    <Row>
                                        <Col xs={12}>
                                            <Row>
                                                <Col>
                                                    <Form onSubmit={handleGeneralSubmit}>
                                                        <UserAddGeneral
                                                            formData={{ ...generalData }}
                                                            profileImage={generalData.profileImagePreview}
                                                            handleChange={handleGeneralChange}
                                                            handleGeneralSubmit={handleGeneralSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                            handleReset={handleGeneralReset}
                                                            companies={companies}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    <ProfileMobileTabs items={profileNavItems} />
                                    <Row>
                                        <Col xs={12}>
                                            <Row className={isMobile ? 'profile-sections-row profile-history-mobile-content profile-mobile-content-card' : 'profile-history-card'}>
                                                <Col>
                                                    {isMobile ? (
                                                        <>
                                                            <h3 className="company-detail-section-title">History of changes</h3>
                                                            <hr />
                                                            <MobileDiscoverySearchToolbar
                                                                searchValue={historySearch}
                                                                onSearchChange={setHistorySearch}
                                                                searchPlaceholder="Search changes..."
                                                                searchAriaLabel="Search history changes"
                                                            />
                                                            {isLoadingHistory ? (
                                                                <p>Loading history...</p>
                                                            ) : mobileHistoryRows.length === 0 ? (
                                                                <p className="text-muted mb-0">{flatRows.length === 0 ? 'No changes recorded yet.' : 'No changes match your search.'}</p>
                                                            ) : (
                                                                <div className="mobile-profile-history-list">
                                                                    {mobileHistoryPagination.mobileItems.map((row) => (
                                                                        <MobileProfileHistoryCard key={row._id} row={row} formatDate={formatDate} fieldLabels={FIELD_LABELS} />
                                                                    ))}
                                                                    <MobileLoadMoreFooter
                                                                        visibleCount={mobileHistoryPagination.visibleCount}
                                                                        totalCount={mobileHistoryPagination.totalCount}
                                                                        itemLabel="changes"
                                                                        onLoadMore={mobileHistoryPagination.loadMore}
                                                                        isLoading={mobileHistoryPagination.isLoadingMore}
                                                                        hasMore={mobileHistoryPagination.hasMore}
                                                                    />
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h5 className="mb-4">History of changes</h5>
                                                            {isLoadingHistory ? (
                                                                <p>Loading history...</p>
                                                            ) : flatRows.length === 0 ? (
                                                                <p className="text-muted">No changes recorded yet.</p>
                                                            ) : <><Table hover responsive>
                                                                <thead>
                                                                    <tr>
                                                                        <th>#</th>
                                                                        <th>Date & Time</th>
                                                                        <th>Section</th>
                                                                        <th>Field</th>
                                                                        <th>Old Value</th>
                                                                        <th>New Value</th>
                                                                        <th>Changed by</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {currentRows.map((row, idx) => (
                                                                        <tr key={`${row.entryId}-${idx}`}>
                                                                            <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                                                                            <td>{formatDate(row.date)}</td>
                                                                            <td>{row.section}</td>
                                                                            <td>{FIELD_LABELS[row.field] || row.field || '—'}</td>
                                                                            <td>{row.oldValue || '—'}</td>
                                                                            <td>{row.newValue || '—'}</td>
                                                                            <td>{row.name}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                            <Row className="pagination-wrapper mt-4">
                                                                <Col className="d-flex align-items-center">
                                                                    <span>Total {flatRows.length} {flatRows.length === 1 ? 'change' : 'changes'}</span>
                                                                    <Pagination>
                                                                        <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>
                                                                            <BsChevronLeft />
                                                                        </Pagination.Prev>
                                                                        {getPaginationItems().map((item, i) =>
                                                                            item === 'ellipsis'
                                                                                ? <Pagination.Ellipsis key={`e-${i}`} disabled />
                                                                                : <Pagination.Item key={item} active={item === currentPage} onClick={() => setCurrentPage(item)}>{item}</Pagination.Item>
                                                                        )}
                                                                        <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0}>
                                                                            <BsChevronRight />
                                                                        </Pagination.Next>
                                                                    </Pagination>
                                                                </Col>
                                                                <Col className="pagination-controls">
                                                                    <div className="pagination-controls-per-page">
                                                                        <Form.Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                                                            <option value="10">10 / page</option>
                                                                            <option value="20">20 / page</option>
                                                                            <option value="40">40 / page</option>
                                                                        </Form.Select>
                                                                    </div>
                                                                    <label className="pagination-controls-goto-label" htmlFor="history-goto-page">Go to</label>
                                                                    <Form.Control
                                                                        id="history-goto-page"
                                                                        className="pagination-controls-goto-input"
                                                                        type="number"
                                                                        value={currentPage}
                                                                        onChange={(e) => { const p = Number(e.target.value); if (p >= 1 && p <= totalPages) setCurrentPage(p); }}
                                                                        onBlur={() => { if (currentPage < 1) setCurrentPage(1); if (currentPage > totalPages) setCurrentPage(totalPages); }}
                                                                    />
                                                                </Col>
                                                            </Row></>}
                                                        </>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="d-flex justify-content-end gap-3 font-size-md mt-4 profile-legal-links">
                                            <a className="text-primary fw-bold" href="/Abroadify-Privacy-Notice.pdf" target="_blank" rel="noopener noreferrer" onClick={(event) => openPublicPdf(event, '/Abroadify-Privacy-Notice.pdf')}>Privacy</a>
                                            <span className="profile-legal-links__separator d-lg-none" aria-hidden="true">·</span>
                                            <a className="text-primary fw-bold" href="/Abroadify-Terms-of-Use.pdf" target="_blank" rel="noopener noreferrer" onClick={(event) => openPublicPdf(event, '/Abroadify-Terms-of-Use.pdf')}>Terms of Use</a>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <div className="buttons-row profile-account-actions">
                        <Row>
                            <Col className="d-flex gap-3 justify-content-end">
                                <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete account</Button>
                                <Button variant="outline-secondary" onClick={() => setShowDeactivateModal(true)}>{isActive ? 'Deactivate account' : 'Activate account'}</Button>
                                <Button variant="outline-secondary" onClick={() => { setShowPasswordModal(true); setPwdMsg(''); setPwdSuccess(''); setNewPwd(''); setConfirmPwd(''); }}>Change password</Button>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </main>

            <ProfileUpdateSuccessModal show={showSavedModal} onHide={() => setShowSavedModal(false)} message={savedModalMessage} icon={icSucess} />
            <ProfileAccountSuccessModal kind={accountSuccessKind} onHide={hideAccountSuccess} />

            <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered className="profile-account-mobile-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{isActive ? 'Deactivate account' : 'Activate account'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{isActive ? 'Are you sure you want to deactivate your account?' : 'Are you sure you want to reactivate your account?'}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeactivateModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleDeactivate}>{isActive ? 'Deactivate' : 'Activate'}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="profile-account-mobile-modal">
                <Modal.Header closeButton><Modal.Title>Delete account</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to permanently delete your account? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDelete}>Delete account</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered className="profile-account-mobile-modal">
                <Modal.Header closeButton><Modal.Title>Change password</Modal.Title></Modal.Header>
                <Form onSubmit={handleChangePassword}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>New password</Form.Label>
                            <div style={{ position: 'relative' }}>
                                <Form.Control
                                    type={showNewPwd ? 'text' : 'password'}
                                    value={newPwd}
                                    onChange={(e) => { setNewPwd(e.target.value); setPwdMsg(''); }}
                                    autoComplete="new-password"
                                    placeholder="Enter new password"
                                    style={{ paddingRight: '42px' }}
                                />
                                <img src={showNewPwd ? icEye : icEyeClosed} alt="" width={16} height={16}
                                    onClick={() => setShowNewPwd(v => !v)}
                                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', opacity: 0.7 }}
                                />
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Confirm new password</Form.Label>
                            <div style={{ position: 'relative' }}>
                                <Form.Control
                                    type={showConfirmPwd ? 'text' : 'password'}
                                    value={confirmPwd}
                                    onChange={(e) => { setConfirmPwd(e.target.value); setPwdMsg(''); }}
                                    autoComplete="new-password"
                                    placeholder="Repeat new password"
                                    style={{ paddingRight: '42px' }}
                                />
                                <img src={showConfirmPwd ? icEye : icEyeClosed} alt="" width={16} height={16}
                                    onClick={() => setShowConfirmPwd(v => !v)}
                                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', opacity: 0.7 }}
                                />
                            </div>
                        </Form.Group>
                        <div className="mt-3">
                            <div className="mb-2 font-size-sm">Create password that contains at least:</div>
                            <div className="row g-2 font-size-sm">
                                <div className="col-6"><div className={pwdChecks.minLen ? 'text-success' : 'text-danger'}>{pwdChecks.minLen ? '✓' : '✕'} min. 8 characters</div></div>
                                <div className="col-6"><div className={pwdChecks.lower ? 'text-success' : 'text-danger'}>{pwdChecks.lower ? '✓' : '✕'} one lowercase letter</div></div>
                                <div className="col-6"><div className={pwdChecks.upper ? 'text-success' : 'text-danger'}>{pwdChecks.upper ? '✓' : '✕'} one capital letter</div></div>
                                <div className="col-6"><div className={pwdChecks.number ? 'text-success' : 'text-danger'}>{pwdChecks.number ? '✓' : '✕'} one number</div></div>
                                <div className="col-12"><div className={pwdChecks.special ? 'text-success' : 'text-danger'}>{pwdChecks.special ? '✓' : '✕'} one special character</div></div>
                                {pwdMatch !== null && (
                                    <div className="col-12"><div className={pwdMatch ? 'text-success' : 'text-danger'}>{pwdMatch ? '✓' : '✕'} passwords match</div></div>
                                )}
                            </div>
                        </div>
                        {pwdMsg && <div className="mt-3 font-size-sm text-danger">{pwdMsg}</div>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button className="me-3" variant="outline-secondary" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={pwdLoading}>{pwdLoading ? 'Saving…' : 'Save'}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default UserProfileHistory;
